import React, { useState, useEffect } from "react";
import {
  TextInput,
  Button,
  Group,
  LoadingOverlay,
  SimpleGrid,
} from "@mantine/core";
import { useForm, isNotEmpty } from "@mantine/form";
import { useAuthUser } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { useServerApi } from "../hooks/useServerApi";
import { FormPage, FormSectionCard, useFormNotification } from "../layout/page";

const Profile = () => {
  const auth = useAuthUser();
  const [api] = useServerApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [notifyFormSuccess, notifyFormError] = useFormNotification();

  const form = useForm({
    initialValues: {
      name: "",
      cname: "",
      aliasName: "",
      phone: "",
      phone2: "",
      personalEmail: "",
      workTitle: "",
    },
    validate: {
      name: isNotEmpty("Name is required"),
      phone: (value) =>
        value && value.length > 0 && value.length < 8
          ? "Phone must have at least 8 digits"
          : null,
    },
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userId = auth()?._id ?? auth()?.id;
        if (!userId) return;
        const user = await api.getById({ apiEntity: "user", id: userId });
        if (user) {
          form.setValues({
            name: user.name ?? "",
            cname: user.cname ?? "",
            aliasName: user.aliasName ?? "",
            phone: user.phone ?? "",
            phone2: user.phone2 ?? "",
            personalEmail: user.personalEmail ?? "",
            workTitle: user.workTitle ?? "",
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleSubmit = async () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    try {
      setSaving(true);
      const userId = auth()?._id ?? auth()?.id;
      const result = await api.updateById({
        apiEntity: "user",
        id: userId,
        values: {
          name: form.values.name,
          cname: form.values.cname,
          aliasName: form.values.aliasName,
          phone: form.values.phone,
          phone2: form.values.phone2,
          personalEmail: form.values.personalEmail,
          workTitle: form.values.workTitle,
        },
      });

      if (!result?.success) {
        notifyFormError({
          title: "Update Failed",
          message: result?.error ?? "Could not save profile.",
        });
        return;
      }

      notifyFormSuccess({
        title: "Profile Saved",
        message: "Your profile has been updated.",
      });
    } catch (err) {
      console.error(err);
      notifyFormError({ title: "Error", message: "An error occurred." });
    } finally {
      setSaving(false);
    }
  };

  const actions = () => null;

  return (
    <FormPage title="My Profile" actions={actions}>
      <div style={{ position: "relative" }}>
        <LoadingOverlay visible={loading} />

        <FormSectionCard title="Basic Information">
          <SimpleGrid cols={2} spacing="md">
            <TextInput
              label="English Name"
              placeholder="Full English name"
              required
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Chinese Name"
              placeholder="中文姓名"
              {...form.getInputProps("cname")}
            />
            <TextInput
              label="Alias Name"
              placeholder="Preferred name or nickname"
              {...form.getInputProps("aliasName")}
            />
            <TextInput
              label="Work Title"
              placeholder="Your work title"
              {...form.getInputProps("workTitle")}
            />
          </SimpleGrid>
        </FormSectionCard>

        <FormSectionCard title="Contact">
          <SimpleGrid cols={2} spacing="md">
            <TextInput
              label="Phone"
              placeholder="Primary phone number"
              {...form.getInputProps("phone")}
            />
            <TextInput
              label="Phone 2"
              placeholder="Secondary phone number"
              {...form.getInputProps("phone2")}
            />
            <TextInput
              label="Personal Email"
              placeholder="Personal email address"
              {...form.getInputProps("personalEmail")}
            />
            <TextInput
              label="Work Email"
              value={auth()?.email ?? ""}
              disabled
              description="Contact admin to change work email."
            />
          </SimpleGrid>
        </FormSectionCard>

        <Group mt="xl">
          <Button onClick={handleSubmit} loading={saving}>
            Save
          </Button>
          <Button variant="subtle" color="gray" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Group>
      </div>
    </FormPage>
  );
};

export default Profile;
